import React, { useCallback, useEffect, useState, ReactElement } from 'react';
import { Flex, Box } from 'rebass';
import { useFormikContext } from 'formik';
import { ErrorMessage } from './ErrorMessage';
import { LegendaryLabel } from './LegendaryLabel';
import { space } from '../../../theme';

type RadioGroupContextProps = {
  name: string;
  selectedValue: string | null;
  setSelectedValue: (value: string | number) => void;
};

interface RadioGroupFieldProps {
  name: string;
  required?: boolean;
  validateOnBlur?: boolean;
  children?: Array<ReactElement | null>;
  variant?: string;
}

const missingRequiredLegend = (children?: Array<ReactElement | null>) => {
  if (!children || children.length < 1) return false;
  const firstChild = children[0];
  return firstChild?.type !== LegendaryLabel;
};

export const RadioGroupFieldContext = React.createContext<
  RadioGroupContextProps
>({
  name: '',
  selectedValue: null,
  setSelectedValue: () => {},
});

export const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  children,
  name,
  variant = 'classicRadioGroup',
}) => {
  useEffect(() => {
    if (missingRequiredLegend(children)) {
      // eslint-disable-next-line no-console
      console.error('For accessibility, a fieldset requires a legend');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedValue, _setSelectedValue] = useState<string | null>(null);
  let {
    errors,
    touched,
    setFieldValue,
    values,
    setFieldTouched,
  } = useFormikContext<any>();

  useEffect(() => {
    _setSelectedValue(values[name]);
  }, [name, values, _setSelectedValue]);

  const setSelectedValue = useCallback(
    (value: string | number) => {
      setFieldValue(name, value);
      setFieldTouched(name, true, false);
    },
    [name, setFieldValue, setFieldTouched]
  );
  const contextValue = { name, selectedValue, setSelectedValue };

  const hasError = !!touched[name] && errors && errors[name];
  const errorName = `errorFor${name[0].toUpperCase() + name.substr(1)}`;

  return (
    <Box>
      <Box
        as="fieldset"
        tx="forms"
        variant={variant}
        aria-describedby={errorName}
      >
        <Flex>
          <RadioGroupFieldContext.Provider value={contextValue}>
            {children}
          </RadioGroupFieldContext.Provider>
        </Flex>
      </Box>
      {hasError && (
        <ErrorMessage
          display="block"
          mt={space.half}
          name={name}
          errors={errors}
          errorName={errorName}
        />
      )}
    </Box>
  );
};
